/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `YC-48` — ĐỘ TƯƠI CỦA SỐ ĐO CÔNG TƠ. **Đầu nhận là chỗ DUY NHẤT chặn được.**
 *
 * Định nghĩa dev_device chốt 09/09 (đặc tả P-3 v2, mục A):
 *
 *     soConTuoi = 1  ⟺  tủ ĐÃ ĐỌC ĐƯỢC công tơ ít nhất MỘT LẦN kể từ khi khởi động
 *                       (`meter_last_ok_ms != 0`)  VÀ  lần đọc thành công gần nhất
 *                       chưa quá 35 000 ms.
 *     soConTuoi = 2  ⟺  tủ khai KHÔNG TƯƠI
 *     soConTuoi = 0  ⟺  TỦ KHÔNG KHAI (tủ đời cũ) — số 0 rơi khỏi dây
 *
 * 🔴 LUẬT: **`soConTuoi != 1` ⇒ KHÔNG ĐƯỢC VẼ SỐ, bất kể số bằng bao nhiêu.**
 *
 * ⛔ VÌ SAO Ở ĐÂY VÀ KHÔNG Ở ĐÂU KHÁC: kho firmware **không có `has_`**
 *   (`grep -c "protobuf_c_boolean has_" = 0`), proto3 không `optional` ⇒ trên dây `0` **không
 *   phân biệt được** với *"không gửi"*. Nghĩa là `voltA = 0` có thể là *"đo được 0 V"* hoặc
 *   *"không có số"* — và không tầng nào phía dưới tách được hai câu ấy. Đầu nhận là nơi cuối
 *   cùng còn một trường KHÁC (`soConTuoi`) để hỏi. Chặn ở đây, hoặc không chặn được ở đâu.
 *
 * 📌 Và vế `!= 0` trong định nghĩa không phải trang trí: thiếu nó thì `meter_last_ok_ms` khởi
 *   tạo `0` làm `soConTuoi = 1` trong **35 giây đầu mỗi lần tủ lên, dù chưa đọc được lần nào**
 *   — một tủ nói dối 35 giây về một con số an toàn. dev_code bắt được lỗi ấy **trước khi
 *   dev_device viết một dòng mã**. Định nghĩa này LÀ bản vá.
 *
 * ⚠ VÀ ĐÂY LÀ CÁI GIÁ, NÓI RA CHỨ KHÔNG GIẤU: tủ **chưa nạp P-3** không gửi trường này ⇒ đọc
 *   ra `'chua-khai'` ⇒ **mọi tủ đang chạy hôm nay thôi hiện điện áp trong ô "Trạng thái sống"**
 *   cho tới khi nạp. Đó là hỏng về phía *"không vẽ một số không chứng minh được"*, tức phía an
 *   toàn — và nó **phải nói ra lý do**, không được chỉ để trống: một ô trống trông y hệt
 *   *"tủ không có số"*, mà sự thật là *"tủ chưa khai độ tươi"*. Hai câu khác nhau.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 HAI SỰ THẬT VỀ **THẾ GIỚI**, không phải về mã — và chúng là cặp khoá của điều kiện
 *    dev_device đặt kèm khi nhận cái giá của `YC-48`, nguyên văn 09/09:
 *
 *      *"Ngày nào máy chủ này phục vụ tủ KHÁCH HÀNG thì phải xét lại trước."*
 *
 * Hôm nay đúng một tủ lab ⇒ chặn vẽ điện áp không ai chịu thiệt. Nhưng điều kiện làm cho
 * quyết định ấy ĐÚNG nằm **ngoài mã**, và không có gì kêu khi nó hết hiệu lực.
 *
 * ⇒ Nên nó thành một CẶP có phép canh cứng (`ok`, không phải `CHỜ NGƯỜI`):
 *
 *      CHI_CO_TU_LAB = true   →  chấp nhận được, không phải xét
 *      CHI_CO_TU_LAB = false  →  BẮT BUỘC `YC48_DA_XET_LAI = true`, nếu không thì ĐỎ
 *
 * ⛔ Đổi `CHI_CO_TU_LAB` thành `false` mà chưa ai xét ⇒ bộ kiểm ĐỎ. Đó là chỗ khác nhau
 *   giữa **một lời hứa** và **một cổng**: lời hứa im lặng khi thế giới đổi; cổng thì kêu.
 * ⚠ Và cổng này KHÔNG chặn phát hành hôm nay — nó chỉ kêu đúng vào ngày điều kiện đổi.
 *   Chặn ngay bây giờ vì một chuyện chưa xảy ra là dựng lại đúng cái bẫy `NV105`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export const CHI_CO_TU_LAB = true;
export const YC48_DA_XET_LAI = false;

export type DocTuoiSoDo = 'tuoi' | 'khong-tuoi' | 'chua-khai' | 'la';

/** Đúng bảng giá trị trong `kapp_v2.proto` — có phép canh buộc hai bản khớp từng số. */
export const MA_TUOI_SO_DO: Record<number, DocTuoiSoDo> = {
  0: 'chua-khai',
  1: 'tuoi',
  2: 'khong-tuoi',
};

export function docSoConTuoi(raw: unknown): DocTuoiSoDo {
  if (raw === undefined || raw === null) return 'chua-khai';
  const v = Number(raw);
  if (!Number.isInteger(v)) return 'la';
  return MA_TUOI_SO_DO[v] ?? 'la';
}

/**
 * 🔴 CỬA CỦA `YC-48`. Một dòng, một chỗ — mọi nơi vẽ số đo công tơ phải đi qua đây.
 *
 * ⛔ Viết bằng `=== 'tuoi'`, KHÔNG viết `!== 'khong-tuoi'`: hai câu ấy khác nhau đúng ở
 *   `'chua-khai'` và `'la'`, và đó chính là hai ca luật này sinh ra để chặn.
 */
export const veDuocSoDo = (n: DocTuoiSoDo): boolean => n === 'tuoi';

/**
 * Vì sao không vẽ — **bắt buộc đi kèm chỗ không vẽ**.
 *
 * Một ô trống không nói được gì; nó bị đọc thành *"tủ không có số"*, mà sự thật có thể là
 * *"tủ chưa khai"* hoặc *"tủ khai là số đã cũ"*. Cùng luật `'chua-biet'` ≠ `'khong-ai'`.
 */
export function cauKhongVeSoDo(n: DocTuoiSoDo): string | null {
  switch (n) {
    case 'tuoi':
      return null;
    case 'khong-tuoi':
      return 'Tủ khai số đo công tơ ĐÃ CŨ (quá 35 giây, hoặc chưa đọc được lần nào từ khi khởi động) '
           + '— không vẽ số, vì một số cũ trông y hệt một số mới.';
    case 'chua-khai':
      return 'Tủ CHƯA KHAI độ tươi của số đo (phần sụn đời cũ, chưa có trường `soConTuoi`) '
           + '— không vẽ số. Đây KHÁC với "tủ không có số đo".';
    case 'la':
      return 'Tủ khai một mã độ tươi NGOÀI BẢNG — không đọc được, nên không vẽ số.';
  }
}

/**
 * Tuổi số đo, chỉ để NÓI, không để quyết.
 * ⛔ `0` = tủ không khai (0 rơi khỏi dây) — KHÔNG phải "vừa đo xong 0 ms".
 */
export function moTaDoTuoiMs(raw: unknown): string | null {
  const v = Number(raw ?? 0);
  if (!Number.isFinite(v) || v <= 0) return null;
  return v < 1000 ? `${Math.trunc(v)} ms trước` : `${(v / 1000).toFixed(1)} giây trước`;
}
